import { Check, Phone } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "@/redux/auth/authSlice";
import ChangePasswordModal from "./change-password-modal";

export default function AccountSection() {
  const isMobile = useIsMobile();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const user = useSelector(selectUser);

  // Helper functions for user data
  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.fullname) return user.fullname;
    if (user?.first_name && user?.last_name)
      return `${user.first_name} ${user.last_name}`;
    if (user?.first_name) return user.first_name;
    return "User";
  };

  const getUserInitials = () => {
    if (user?.name) {
      const names = user.name.split(" ");
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return user.name[0].toUpperCase();
    }
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.fullname) {
      const names = user.fullname.split(" ");
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return user.fullname[0].toUpperCase();
    }
    if (user?.first_name) {
      return user.first_name[0].toUpperCase();
    }
    return "U";
  };

  const isEmailVerified = () => {
    return (
      user?.email_verified_at !== null && user?.email_verified_at !== undefined
    );
  };

  return (
    <div className={`${isMobile ? "p-0" : "flex-1 p-6"}`}>
      <div className={`${isMobile ? "" : "max-w-2xl"}`}>
        {!isMobile && (
          <div className="mb-8">
            <h2 className="text-[18px] font-normal text-gray-900 mb-2">
              Your Account
            </h2>
            <p className="text-gray-600">Below are your personal details</p>
          </div>
        )}

        {/* User Information */}
        <div
          className={`flex items-center gap-4 mb-8 ${isMobile ? "mb-6" : ""}`}
        >
          <Avatar className="h-16 w-16 bg-primary">
            <AvatarFallback className="bg-primary text-white text-lg font-semibold">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {getUserDisplayName()}
            </h3>
            <p className="text-gray-600 font-normal text-[12px]">
              Vendor account
            </p>
          </div>
        </div>

        {!isMobile && <Separator className="mb-8" />}

        {/* Email Address Section */}
        <div className={`mb-8 ${isMobile ? "mb-6" : ""}`}>
          <div
            className={`${isMobile ? "space-y-3" : "flex items-start justify-between"}`}
          >
            <div className="flex-1">
              <h4 className="text-sm text-[14px] text-gray-900 mb-1">
                Email address
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                This email account is associated with your account
              </p>
            </div>
            <div className={`${isMobile ? "" : "text-right"}`}>
              <p className="font-medium text-[14px] text-gray-900 mb-1">
                {user?.email || "No email provided"}
              </p>
              {isEmailVerified() ? (
                <div className="flex items-center gap-1 text-primary">
                  <Check className="h-4 w-4" />
                  <span className="text-sm">Verified</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-orange-600">
                  <span className="text-sm">Not verified</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isMobile && <Separator className="mb-8" />}

        {/* Password Section */}
        <div className={`mb-8 ${isMobile ? "mb-6" : ""}`}>
          <div
            className={`${isMobile ? "space-y-3" : "flex items-start justify-between"}`}
          >
            <div className="flex-1">
              <h4 className="text-sm text-[14px] text-gray-900 mb-1">
                Password
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Set up a unique password to protect your account
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowChangePasswordModal(true)}
              className={`bg-primary/10 border-primary/20 text-primary hover:bg-primary/20 hover:text-primary ${
                isMobile ? "w-full h-10" : "w-[137px] h-[33.49px]"
              }`}
            >
              Change Password
            </Button>
          </div>
        </div>

        {/* Phone Number Section */}
        {user?.phone && (
          <>
            {!isMobile && <Separator className="mb-8" />}
            <div className={`mb-8 ${isMobile ? "mb-6" : ""}`}>
              <div
                className={`${isMobile ? "space-y-3" : "flex items-start justify-between"}`}
              >
                <div className="flex-1">
                  <h4 className="text-sm text-[14px] text-gray-900 mb-1">
                    Phone number
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Your contact phone number
                  </p>
                </div>
                <div className={`${isMobile ? "" : "text-right"}`}>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <p className="font-medium text-[14px] text-gray-900">
                      {user.phone}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* User ID Section */}
        {user?.id && (
          <>
            {!isMobile && <Separator className="mb-8" />}
            <div className={`mb-8 ${isMobile ? "mb-6" : ""}`}>
              <div
                className={`${isMobile ? "space-y-3" : "flex items-start justify-between"}`}
              >
                <div className="flex-1">
                  <h4 className="text-sm text-[14px] text-gray-900 mb-1">
                    User ID
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Your unique account identifier
                  </p>
                </div>
                <div className={`${isMobile ? "" : "text-right"}`}>
                  <p className="font-medium text-[14px] text-gray-900 font-mono">
                    {user.id}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Save Changes Button */}
        <div className={`${isMobile ? "mt-8" : "flex justify-end"}`}>
          <Button
            className={`bg-primary hover:bg-primary/90 text-white ${
              isMobile ? "w-full h-12 text-base" : "px-6 py-2"
            }`}
          >
            Save changes
          </Button>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        show={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </div>
  );
}
